import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

/**
 * 提取根域名，用于跨子域名共享 cookie
 * @param hostname 当前 hostname
 * @returns 根域名（带前导点）或原 hostname（开发环境）
 */
const getRootDomain = (hostname: string): string => {
  // 开发环境或 IP 地址，直接返回
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  ) {
    return hostname;
  }

  // 提取根域名
  const parts = hostname.split('.');

  // 已知的二级顶级域名（SLD）列表，如 .co.uk, .com.au 等
  const secondLevelDomains = ['co', 'com', 'net', 'org', 'gov', 'edu'];

  // 如果域名部分数 >= 3，检查倒数第二个部分是否是二级顶级域名
  if (parts.length >= 3) {
    // 如果倒数第二个部分是二级顶级域名（如 listsync.co.uk 中的 'co'）
    // 则取最后三个部分作为根域名（.listsync.co.uk）
    if (secondLevelDomains.includes(parts[parts.length - 2])) {
      const rootDomain = parts.slice(-3).join('.');
      return `.${rootDomain}`;
    }
    // 否则取最后两个部分（如 www.nestflo.ai -> .nestflo.ai）
    const rootDomain = parts.slice(-2).join('.');
    return `.${rootDomain}`;
  }

  // 如果只有两个部分，直接使用（如 nestflo.ai -> .nestflo.ai）
  if (parts.length === 2) {
    const rootDomain = parts.join('.');
    return `.${rootDomain}`;
  }

  // 如果无法确定，返回原 hostname
  return hostname;
};

// React Hook 用于在组件中使用
export const useExternalSource = () => {
  const router = useRouter();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (router.isReady && !hasProcessedRef.current) {
      const params = router.query;
      if (params && params.tracking) {
        const source = params.tracking as string;
        //把 source 保存到 cookie 中，域名设置为根域名（支持跨子域名），永不过期
        const expiresDate = new Date();
        expiresDate.setFullYear(expiresDate.getFullYear() + 10);
        const expiresString = expiresDate.toUTCString();
        const rootDomain = getRootDomain(window.location.hostname);
        document.cookie = `source=${source}; path=/; domain=${rootDomain}; expires=${expiresString}; SameSite=Lax`;
        hasProcessedRef.current = true;
      }
    }
  }, [router.isReady, router.query]);
};
